/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { TFunction } from 'react-i18next';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { CrudListType, CrudTemplate, ListColumns } from 'app/components/organisms/crud/form-types';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { Pharmacy } from 'app/types/pharmacy';
import { pickBy, identity } from 'lodash';

/**
 * `PharmacyUserStats` type.
 */

type PharmacyUserStats = Pharmacy & { userCount: number };

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns<PharmacyUserStats> => [
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.city'),
    dataIndex: 'city',
    key: 'city'
  },
  {
    title: translate('common.table.columns.address'),
    dataIndex: 'address',
    key: 'address',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.postalCode'),
    dataIndex: 'postalCode',
    key: 'postalCode'
  },
  {
    title: translate('common.table.columns.userCount'),
    dataIndex: 'userCount',
    key: 'userCount',
    sortable: true,
    render: item => item.userCount ?? 0
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<PharmacyUserStats> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.pharmacies.userStats,
    canEdit: false,
    canRemove: false,
    key: ['pharmaciesUserStats'],
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100]
    },
    endpoint: apiEndpoints.pharmaciesUserStats,
    normalizeFilters: (data: any) => {
      const { nonMember, search, ...rest } = data.filters ?? {};
      return {
        filters: pickBy({ ...rest, search }, identity),
        ...(nonMember ? { nonMember: true } : {})
      };
    }
  };
}

/**
 * Export `createPharmaciesUserStatsTemplate`.
 */

export function createPharmaciesUserStatsTemplate(translate: TFunction): CrudTemplate<PharmacyUserStats> {
  return normalizeTemplate(translate, {
    list: list(translate)
  });
}
